import { StatusPill } from "@flexfreight/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { api } from "../api/client";

type Notice = { id: string; label: string; count: number; to: string; tone: "danger" | "warning" | "info" };

/** Top-bar bell: live counts of what needs attention for this role (/v1/console/notifications), every minute. */
export function NotificationBell() {
  const q = useQuery({ queryKey: ["console", "notifications"], queryFn: () => api<{ total: number; items: Notice[] }>("/v1/console/notifications"), refetchInterval: 60_000 });
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useId();
  const { pathname, search } = useLocation();
  useEffect(() => setOpen(false), [pathname, search]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const total = q.data?.total ?? 0;
  return (
    <div className="ffa-bell" ref={root}>
      <button ref={button} type="button" className="ffa-bell__btn" aria-expanded={open} aria-controls={panel} onClick={() => setOpen((o) => !o)}>
        <svg viewBox="0 0 20 20" aria-hidden="true">
          <path d="M10 3a4.5 4.5 0 0 0-4.5 4.5v2.8L4 13h12l-1.5-2.7V7.5A4.5 4.5 0 0 0 10 3Z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M8.2 15.5a1.9 1.9 0 0 0 3.6 0" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <span className="ffx-sr">{total ? `${total} things need attention` : "Nothing needs attention"}</span>
        {total ? <span className="ffa-bell__badge" aria-hidden="true">{total > 99 ? "99+" : total}</span> : null}
      </button>
      <div id={panel} className="ffa-bell__panel" hidden={!open}>
        <p className="ffa-bell__title">Needs attention</p>
        {q.isError ? (
          <p className="ffa-muted ffa-small">Couldn&apos;t load right now.</p>
        ) : !q.data ? (
          <p className="ffa-muted ffa-small">Loading...</p>
        ) : q.data.items.length === 0 ? (
          <p className="ffa-muted ffa-small">You&apos;re all caught up.</p>
        ) : (
          <ul className="ffa-bell__list">
            {q.data.items.map((n) => (
              <li key={n.id}>
                <Link to={n.to} className="ffa-bell__item">
                  <span>{n.label}</span>
                  <StatusPill tone={n.tone}>{n.count}</StatusPill>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
